import { VaultApiMode, VaultPerformanceRange } from '@/app/shared/services/api/types';
import { PerformanceChartPeriod } from '@/app/shared/ui/PerformanceChart/types';

export const VAULT_TRANCHE = {
  SENIOR: 'senior',
  JUNIOR: 'junior',
} as const;

export type VaultTrancheId = (typeof VAULT_TRANCHE)[keyof typeof VAULT_TRANCHE];

export type VaultChartSeriesKey = VaultTrancheId;

export const VAULT_TRANCHE_IDS = [VAULT_TRANCHE.SENIOR, VAULT_TRANCHE.JUNIOR] as const;

export const VAULT_TRANCHE_LABEL: Record<VaultTrancheId, string> = {
  [VAULT_TRANCHE.SENIOR]: 'Senior',
  [VAULT_TRANCHE.JUNIOR]: 'Junior',
};

export const VAULT_TRANCHE_TITLE: Record<VaultTrancheId, string> = {
  [VAULT_TRANCHE.SENIOR]: 'Senior Tranche',
  [VAULT_TRANCHE.JUNIOR]: 'Junior Tranche',
};

export interface VaultTrancheOption {
  id: VaultTrancheId;
  title: string;
}

export const VAULT_TRANCHES: VaultTrancheOption[] = [
  { id: VAULT_TRANCHE.SENIOR, title: VAULT_TRANCHE_TITLE[VAULT_TRANCHE.SENIOR] },
  { id: VAULT_TRANCHE.JUNIOR, title: VAULT_TRANCHE_TITLE[VAULT_TRANCHE.JUNIOR] },
];

export const VAULT_TRANCHE_INFO: Record<VaultTrancheId, string> = {
  [VAULT_TRANCHE.SENIOR]:
    'Senior tranches are protected by the Junior buffer and only absorb losses once Junior tranches are exhausted. During withdrawal, this example receives 1× the underlying Prime yield. The request starts at the end of the current epoch, and the final value may change before completion.',
  [VAULT_TRANCHE.JUNIOR]:
    'Junior absorbs losses first and has direct exposure to strategy losses in exchange for the highest yield. When this tranche is above its target allocation, its yield is reduced.',
};

export type RateBasis = 'current' | 'avg7' | 'avg30';

export const MODE_BY_BASIS: Record<RateBasis, VaultApiMode> = {
  current: 'current',
  avg7: '7d',
  avg30: '30d',
};

export const RANGE_BY_PERIOD: Record<PerformanceChartPeriod, VaultPerformanceRange> = {
  [PerformanceChartPeriod.WEEK]: '7d',
  [PerformanceChartPeriod.MONTH]: '30d',
  [PerformanceChartPeriod.QUARTER]: '90d',
  [PerformanceChartPeriod.ALL_TIME]: 'all',
};

export const SERIES_COLORS = {
  base: 'var(--chart-base)',
  [VAULT_TRANCHE.SENIOR]: 'var(--chart-senior)',
  [VAULT_TRANCHE.JUNIOR]: 'var(--chart-junior)',
} as const;

export const BASIS_TABS = [
  { id: 'current' as const, content: 'Current' },
  { id: 'avg7' as const, content: '7D avg' },
  { id: 'avg30' as const, content: '30D avg' },
];

const HASTRA_VAULT_DESCRIPTION =
  'The Hastra Optimizer Vault deploys wYLDS into Hastra AUTO and Hastra PRIME, optimizing for the best yields between opportunities both on SVM and EVM ecosystems, this often includes leverage, lendside yields, and liquidity provisioning.\n\nYield is split into a base reward paid to both tranches and a risk premium paid only to mjHastra, which earns a higher rate for absorbing losses first. msHastra receives the base reward alone and takes losses only after mjHastra is depleted.';

export const VAULT_DESCRIPTIONS: Record<string, string> = {
  // Sepolia (pre-dev / develop)
  '0xd95d5319e98e2583fb2c15a196cea58fabf8e07a': HASTRA_VAULT_DESCRIPTION,
  // Mainnet
  '0x55a0cb0cacbadd0fe92571768cc256e92f1b857d': HASTRA_VAULT_DESCRIPTION,
  '0xbb03549117e38103589774ae1d6338d1b237ffa2': HASTRA_VAULT_DESCRIPTION,
};

export const getVaultDescription = (vaultId?: string): string | undefined => {
  if (!vaultId) return undefined;
  return VAULT_DESCRIPTIONS[vaultId.toLowerCase()];
};
